import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useParams, NavLink } from "react-router-dom";
import { Container, Row, Col, Form } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaTruck, FaComments, FaFileInvoice } from "react-icons/fa";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import "./productdetils.css";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

const page = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
      when: "beforeChildren",
      staggerChildren: 0.08,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
};

const Productdetails = () => {
  const { id } = useParams();
  const { pathname } = useLocation();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [activeImages, setActiveImages] = useState([]);
  const [activeImg, setActiveImg] = useState("");
  const [qty, setQty] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedMemory, setSelectedMemory] = useState("");
  const [selectedStorage, setSelectedStorage] = useState("");
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [availableVariants, setAvailableVariants] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [availableProcessors, setAvailableProcessors] = useState([]);
  const [selectedProcessor, setSelectedProcessor] = useState("");
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [selectedBand, setSelectedBand] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedConnectivity, setSelectedConnectivity] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  const getImagesForColor = useCallback((colorObj) => {
    if (!colorObj) return [];

    if (colorObj.images) {
      if (Array.isArray(colorObj.images)) {
        return colorObj.images;
      }
      if (typeof colorObj.images === "object") {
        return Object.values(colorObj.images).filter(Boolean);
      }
    }

    if (colorObj.image) {
      return [colorObj.image];
    }

    return [];
  }, []);

  const getDefaultImages = useCallback((productData) => {
    if (!productData) return [];

    if (Array.isArray(productData.images) && productData.images.length > 0) {
      console.log("Using top-level images array:", productData.images);
      return productData.images;
    }

    if (productData.colors && Array.isArray(productData.colors) && productData.colors.length > 0) {
      const firstColor = productData.colors[0];
      if (firstColor.images) {
        if (Array.isArray(firstColor.images) && firstColor.images.length > 0) {
          return firstColor.images;
        }
        if (typeof firstColor.images === 'object') {
          const images = Object.values(firstColor.images).filter(Boolean);
          if (images.length > 0) return images;
        }
      }
      if (firstColor.image) {
        return [firstColor.image];
      }
    }

    if (productData.images && typeof productData.images === 'object' && !Array.isArray(productData.images)) {
      const firstColorKey = Object.keys(productData.images)[0];
      if (firstColorKey) {
        const colorImages = productData.images[firstColorKey];
        if (Array.isArray(colorImages)) return colorImages;
        if (typeof colorImages === 'string') return [colorImages];
        if (typeof colorImages === 'object') return Object.values(colorImages).filter(Boolean);
      }
    }

    if (productData.variants && Array.isArray(productData.variants)) {
      const variantWithImage = productData.variants.find(v => v.image);
      if (variantWithImage?.image) {
        return [variantWithImage.image];
      }
    }

    return [];
  }, []);

  useEffect(() => {
    if (!product) return;

    let images = [];

    if (selectedColor) {
      const colorImages = getImagesForColor(selectedColor);
      if (colorImages.length > 0) {
        console.log("Using color-specific images for:", selectedColor.name);
        images = colorImages;
      }
    }

    if (images.length === 0) {
      console.log("No color-specific images, using default images");
      images = getDefaultImages(product);
    }

    setActiveImages(images);
    setActiveImg(images[0] || "");
  }, [selectedColor, product]);

  useEffect(() => {
    if (!product) return;

    console.log("Initializing product data:", product);
    console.log("Product colors:", product.colors);
    console.log("Product images structure:", product.images);
    
    if (product.colors && product.colors.length > 0) {
      console.log("First color images:", product.colors[0].images);
    }

    if (product.variants?.length > 0) {
      setAvailableVariants(product.variants);
    }

    if (product.colors?.length > 0) {
      console.log("Setting initial color:", product.colors[0]);
      setSelectedColor(product.colors[0]);
    }

    if (product.processor) {
      const processors = Array.isArray(product.processor)
        ? product.processor
        : [product.processor];
      setAvailableProcessors(processors);
      setSelectedProcessor(processors[0]);
    }

    if (product.memory?.length > 0) {
      setSelectedMemory(product.memory[0]);
    }

    if (product.storage?.length > 0) {
      setSelectedStorage(product.storage[0]);
    }

    if (product.variants?.length > 0) {
      setSelectedVariant(product.variants[0]);
      setCurrentPrice(product.variants[0].price || product.price);
    } else {
      setCurrentPrice(product.price);
    }

    if (product.variants?.length > 0) {
      const bands = [...new Set(product.variants.map(v => v.band).filter(Boolean))];
      if (bands.length > 0) {
        console.log("Setting initial band:", bands[0]);
        setSelectedBand(bands[0]);
      }

      const sizes = [...new Set(product.variants.map(v => v.size).filter(Boolean))];
      if (sizes.length > 0) {
        console.log("Setting initial size:", sizes[0]);
        setSelectedSize(sizes[0]);
      }

      const connectivities = [...new Set(product.variants.map(v => v.connectivity).filter(Boolean))];
      if (connectivities.length > 0) {
        setSelectedConnectivity(connectivities[0]);
      }
    }
  }, [product]);

  const fetchRelatedProducts = useCallback(async (currentProduct) => {
    setLoadingRelated(true);

    try {
      const currentId = currentProduct._id?.$oid || currentProduct._id;

      if (currentProduct.category) {
        const url = `${REACT_APP_API_URL}/products?category=${encodeURIComponent(currentProduct.category)}&limit=5`;
        const res = await fetch(url);

        if (res.ok) {
          const data = await res.json();
          const related = data.filter(p => {
            const pId = p._id?.$oid || p._id;
            return pId !== currentId;
          }).slice(0, 4);

          if (related.length > 0) {
            setRelatedProducts(related);
            setLoadingRelated(false);
            return;
          }
        }
      }

      const res = await fetch(`${REACT_APP_API_URL}/products?limit=10`);
      if (res.ok) {
        const data = await res.json();
        const random = data
          .filter(p => {
            const pId = p._id?.$oid || p._id;
            return pId !== currentId;
          })
          .sort(() => 0.5 - Math.random())
          .slice(0, 4);
        setRelatedProducts(random);
      }
    } catch (err) {
      console.error("Failed to fetch related products:", err);
    } finally {
      setLoadingRelated(false);
    }
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetch(`${REACT_APP_API_URL}/products/${id}`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

        const data = await res.json();
        console.log("Product data loaded:", data);
        setProduct(data);

        setTimeout(() => fetchRelatedProducts(data), 100);
      } catch (err) {
        console.error("Failed to fetch product", err);
      }
    };

    fetchProduct();
  }, [id, fetchRelatedProducts]);

  if (!product) return null;

  const formatMemory = (mem) => {
    if (!mem) return mem;
    return product.category?.toLowerCase().includes("mac")
      ? `${mem} unified memory`
      : mem;
  };

  const formatStorage = (sto) => {
    if (!sto) return sto;
    return sto.includes("SSD") ? sto : `${sto}`;
  };

  const handleColorSelect = (color) => {
    console.log("Selected color:", color);
    setSelectedColor(color);

    if (product.variants?.length > 0) {
      const colorVariants = product.variants.filter(v => v.color === color.name);
      if (colorVariants.length > 0) {
        const matchingVariant = colorVariants.find(v =>
          (!selectedStorage || v.storage === selectedStorage) &&
          (!selectedMemory || v.memory === selectedMemory) &&
          (!selectedBand || v.band === selectedBand) &&
          (!selectedSize || v.size === selectedSize)
        ) || colorVariants[0];

        console.log("Matching variant:", matchingVariant);
        setSelectedVariant(matchingVariant);
        setCurrentPrice(matchingVariant.price || product.price);

        if (matchingVariant.storage) setSelectedStorage(matchingVariant.storage);
        if (matchingVariant.memory) setSelectedMemory(matchingVariant.memory);
        if (matchingVariant.band) setSelectedBand(matchingVariant.band);
        if (matchingVariant.size) setSelectedSize(matchingVariant.size);
      }
    }
  };

  const getExactSelectedVariant = () => {
    if (!product?.variants?.length) return null;

    let matchingVariant = product.variants.find(v =>
      (!selectedColor || v.color === selectedColor.name) &&
      (!selectedStorage || v.storage === selectedStorage) &&
      (!selectedMemory || v.memory === selectedMemory) &&
      (!selectedBand || v.band === selectedBand) &&
      (!selectedSize || v.size === selectedSize) &&
      (!selectedProcessor || v.processor === selectedProcessor) &&
      (!selectedConnectivity || v.connectivity === selectedConnectivity)
    );

    return matchingVariant || null;
  };
  
  const handleVariantSelect = (type, value) => {
    console.log(`Selected ${type}:`, value);

    if (type === 'storage') setSelectedStorage(value);
    if (type === 'memory') setSelectedMemory(value);
    if (type === 'band') setSelectedBand(value);
    if (type === 'size') setSelectedSize(value);
    if (type === 'processor') setSelectedProcessor(value);
    if (type === 'connectivity') setSelectedConnectivity(value);

    if (product.variants?.length > 0 && selectedColor) {
      const matchingVariant = product.variants.find(v =>
        v.color === selectedColor.name &&
        (type === 'storage' ? v.storage === value : (!value || v.storage === selectedStorage)) &&
        (type === 'memory' ? v.memory === value : (!value || v.memory === selectedMemory)) &&
        (type === 'band' ? v.band === value : (!value || v.band === selectedBand)) &&
        (type === 'size' ? v.size === value : (!value || v.size === selectedSize)) &&
        (type === 'processor' ? v.processor === value : (!value || v.processor === selectedProcessor)) &&
        (type === 'connectivity' ? v.connectivity === value : (!value || v.connectivity === selectedConnectivity))
      );

      if (matchingVariant) {
        console.log("Found matching variant:", matchingVariant);
        setSelectedVariant(matchingVariant);
        setCurrentPrice(matchingVariant.price || product.price);
      }
    }
  };

  const getUniqueColors = () => {
    if (product.colors?.length > 0) {
      return product.colors;
    }

    if (product.variants?.length > 0) {
      const unique = [...new Set(product.variants.map(v => v.color))];
      return unique.map(colorName => ({
        name: colorName,
        hex: "#000000"
      }));
    }

    return [];
  };

  const getUniqueStorages = () => {
    if (!selectedColor) return product.storage || [];
    if (product.variants?.length > 0) {
      return [...new Set(
        product.variants
          .filter(v => v.color === selectedColor.name && v.storage)
          .map(v => v.storage)
      )];
    }
    return product.storage || [];
  };

  const getUniqueMemories = () => {
    if (!selectedColor || !selectedStorage) return product.memory || [];
    if (product.variants?.length > 0) {
      return [...new Set(
        product.variants
          .filter(v => v.color === selectedColor.name && v.storage === selectedStorage && v.memory)
          .map(v => v.memory)
      )];
    }
    return product.memory || [];
  };

  const getUniqueBands = () => {
    if (!selectedColor) return [];
    if (product.variants?.length > 0) {
      return [...new Set(
        product.variants
          .filter(v => v.color === selectedColor.name && v.band)
          .map(v => v.band)
      )];
    }
    return [];
  };

  const getUniqueSizes = () => {
    if (!selectedColor) return [];
    if (product.variants?.length > 0) {
      return [...new Set(
        product.variants
          .filter(v => v.color === selectedColor.name && v.size)
          .map(v => v.size)
      )];
    }
    return product.caseSize ? [product.caseSize] : [];
  };

  const getPriceForStorage = (storage) => {
    if (!selectedColor || !product.variants?.length) return null;

    const variants = product.variants.filter(
      v => v.color === selectedColor.name && v.storage === storage
    );

    if (variants.length === 0) return null;
    if (variants.length === 1) return variants[0].price;

    const prices = variants.map(v => v.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    if (minPrice === maxPrice) return minPrice;

    const matchingVariant = variants.find(v => v.memory === selectedMemory);
    return matchingVariant?.price || minPrice;
  };

  const handleAddToCart = async () => {
    try {
      const token = localStorage.getItem("token");
      const guestId = localStorage.getItem("guestId");

      let finalPrice = product.price;
      let sku = product.sku || "";
      let exactVariant = null;

      if (product.variants?.length > 0) {
        exactVariant = getExactSelectedVariant();
        
        if (!exactVariant && selectedStorage) {
          exactVariant = product.variants.find(v => v.storage === selectedStorage);
        }
        
        if (!exactVariant && selectedColor) {
          exactVariant = product.variants.find(v => v.color === selectedColor.name);
        }
        
        if (!exactVariant) {
          exactVariant = product.variants[0];
        }

        if (exactVariant) {
          finalPrice = exactVariant.price;
          sku = exactVariant.sku;
          
          if (exactVariant.storage) setSelectedStorage(exactVariant.storage);
          if (exactVariant.memory) setSelectedMemory(exactVariant.memory);
          if (exactVariant.processor) setSelectedProcessor(exactVariant.processor);
          if (exactVariant.band) setSelectedBand(exactVariant.band);
          if (exactVariant.size) setSelectedSize(exactVariant.size);
          if (exactVariant.connectivity) setSelectedConnectivity(exactVariant.connectivity);
        }
      }

      const cartProduct = {
        productId: product._id,
        name: product.name,
        price: finalPrice,
        quantity: qty,
        image: activeImg || "/placeholder.jpg",
        sku: sku,
        color: selectedColor?.name || "",
        memory: selectedMemory || "",
        storage: selectedStorage || "",
        processor: selectedProcessor || "",
        band: selectedBand || "",
        size: selectedSize || "",
        connectivity: selectedConnectivity || "",
      };

      console.log("Adding to cart:", cartProduct);

      const response = await fetch(`${REACT_APP_API_URL}/cart/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          guestId,
          product: cartProduct,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        alert("Failed to add to cart: " + error.message);
        return;
      }

      window.location.href = "/cart";
    } catch (err) {
      console.error("Add to cart failed", err);
      alert("Network error - please try again");
    }
  };

  const getRelatedProductPrice = (product) => {
    if (product.price) {
      return `₹${product.price.toLocaleString()}`;
    }

    if (product.variants?.length > 0) {
      const prices = product.variants.map(v => v.price).filter(Boolean);
      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        return minPrice === maxPrice
          ? `₹${minPrice.toLocaleString()}`
          : `₹${minPrice.toLocaleString()} - ₹${maxPrice.toLocaleString()}`;
      }
    }

    return "Price on request";
  };

  const getRelatedProductImage = (relatedProduct) => {
    if (relatedProduct.colors && relatedProduct.colors.length > 0) {
      const firstColor = relatedProduct.colors[0];
      if (firstColor.images && Array.isArray(firstColor.images) && firstColor.images.length > 0) {
        return firstColor.images[0];
      }
      if (firstColor.image) {
        return firstColor.image;
      }
    }

    if (relatedProduct.images && typeof relatedProduct.images === 'object' && !Array.isArray(relatedProduct.images)) {
      const firstColor = Object.keys(relatedProduct.images)[0];
      if (firstColor) {
        const colorImages = relatedProduct.images[firstColor];
        if (Array.isArray(colorImages) && colorImages.length > 0) {
          return colorImages[0];
        } else if (typeof colorImages === 'string') {
          return colorImages;
        } else if (typeof colorImages === 'object') {
          const values = Object.values(colorImages);
          if (values.length > 0) {
            return values[0];
          }
        }
      }
    } 
    else if (Array.isArray(relatedProduct.images) && relatedProduct.images.length > 0) {
      return relatedProduct.images[0];
    }
    
    if (relatedProduct.variants && relatedProduct.variants.length > 0) {
      const variantWithImage = relatedProduct.variants.find(v => v.image);
      if (variantWithImage?.image) {
        return variantWithImage.image;
      }
    }
    
    return "/placeholder.jpg";
  };

  const uniqueColors = getUniqueColors();
  const uniqueStorages = getUniqueStorages();
  const uniqueMemories = getUniqueMemories();
  const uniqueBands = getUniqueBands();
  const uniqueSizes = getUniqueSizes();

  const displayPrice = currentPrice || product.price || 0;

  return (
    <div>
      <Header />

      <motion.div variants={page} initial="hidden" animate="show">
        <Container fluid className="product-wrapper">
          <Row>
            {activeImages.length > 0 && (
              <Col lg={1} md={2} className="thumbs">
                {activeImages.map((img) => (
                  <motion.img
                    key={img}
                    src={img}
                    alt="thumb"
                    className={activeImg === img ? "active" : ""}
                    onClick={() => setActiveImg(img)}
                    variants={fadeUp}
                    whileHover={{ scale: 1.18 }}
                    whileTap={{ scale: 0.9 }}
                    onError={(e) => (e.target.src = "/placeholder.jpg")}
                  />
                ))}
              </Col>
            )}

            <Col lg={activeImages.length > 0 ? 5 : 6} md={activeImages.length > 0 ? 5 : 6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                style={{ perspective: 1200 }}
              >
                {activeImg ? (
                  <motion.img
                    key={activeImg}
                    src={activeImg}
                    alt={product.name}
                    className="main-img"
                    animate={{ y: [0, -8, 0] }}
                    transition={{
                      y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
                    }}
                    whileHover={{ rotateX: 4, rotateY: -6, scale: 1.05 }}
                    onError={(e) => {
                      console.log("Main image failed to load:", activeImg);
                      e.target.src = "/placeholder.jpg";
                    }}
                  />
                ) : (
                  <div className="main-img-placeholder">
                    <img 
                      src="/placeholder.jpg" 
                      alt="No image available"
                      className="main-img-placeholder-img"
                    />
                  </div>
                )}
              </motion.div>
            </Col>

            <Col lg={activeImages.length > 0 ? 6 : 6} md={activeImages.length > 0 ? 5 : 6}>
              <motion.div className="product-info">
                <motion.h3 variants={fadeUp}>{product.name}</motion.h3>

                {product.description && (
                  <motion.p className="description" variants={fadeUp}>
                    {product.description}
                  </motion.p>
                )}

                <motion.p className="price" variants={fadeUp}>
                  ₹{displayPrice.toLocaleString()}
                </motion.p>

                <motion.span
                  className="stock"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 1.4, repeat: Infinity }}
                >
                  In stock
                </motion.span>

                <ul className="info-list">
                  {[FaComments, FaTruck, FaFileInvoice].map((Icon, i) => (
                    <motion.li key={i} variants={fadeUp}>
                      <Icon />
                      {i === 0 && " Talk to our Expert on Chat"}
                      {i === 1 && " Free Shipping Across India"}
                      {i === 2 && " GST Invoice Available"}
                    </motion.li>
                  ))}
                </ul>

                {uniqueColors.length > 0 && (
                  <motion.div className="option" variants={fadeUp}>
                    <label>Color</label>
                    <div className="colors">
                      {uniqueColors.map((color) => {
                        const colorHex = color.hex || "#000000";
                        const isSelected = selectedColor?.name === color.name;
                        return (
                          <motion.span
                            key={color.name}
                            className={`color ${isSelected ? "active" : ""}`}
                            style={{ backgroundColor: colorHex }}
                            onClick={() => handleColorSelect(color)}
                            whileHover={{ scale: 1.3 }}
                            whileTap={{ scale: 0.9 }}
                            title={color.name}
                          />
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {uniqueSizes.length > 0 && (
                  <motion.div className="option" variants={fadeUp}>
                    <label>Size</label>
                    <div className="box-options">
                      {uniqueSizes.map((size) => (
                        <motion.span
                          key={size}
                          className={`box-option ${selectedSize === size ? "active" : ""}`}
                          onClick={() => handleVariantSelect('size', size)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          {size}
                        </motion.span>
                      ))}
                    </div>
                  </motion.div>
                )}

                {uniqueBands.length > 0 && (
                  <motion.div className="option" variants={fadeUp}>
                    <label>Band</label>
                    <div className="box-options">
                      {uniqueBands.map((band) => (
                        <motion.span
                          key={band}
                          className={`box-option ${selectedBand === band ? "active" : ""}`}
                          onClick={() => handleVariantSelect('band', band)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          {band}
                        </motion.span>
                      ))}
                    </div>
                  </motion.div>
                )}

                {availableProcessors.length > 0 && (
                  <motion.div className="option" variants={fadeUp}>
                    <label>Processor</label>
                    <Form.Select
                      value={selectedProcessor}
                      onChange={(e) => handleVariantSelect('processor', e.target.value)}
                    >
                      {availableProcessors.map((proc, index) => (
                        <option key={index} value={proc}>{proc}</option>
                      ))}
                    </Form.Select>
                  </motion.div>
                )}

                {uniqueMemories.length > 0 && (
                  <motion.div className="option" variants={fadeUp}>
                    <label>Memory</label>
                    <div className="box-options">
                      {uniqueMemories.map((mem) => (
                        <motion.span
                          key={mem}
                          className={`box-option ${selectedMemory === mem ? "active" : ""}`}
                          onClick={() => handleVariantSelect('memory', mem)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          {formatMemory(mem)}
                        </motion.span>
                      ))}
                    </div>
                  </motion.div>
                )}

                {uniqueStorages.length > 0 && (
                  <motion.div className="option" variants={fadeUp}>
                    <label>Storage</label>
                    <div className="box-options storage-options">
                      {uniqueStorages.map((sto) => {
                        const priceForStorage = getPriceForStorage(sto);
                        const isSelected = selectedStorage === sto;

                        return (
                          <motion.div
                            key={sto}
                            className={`storage-card ${isSelected ? "active" : ""}`}
                            onClick={() => handleVariantSelect('storage', sto)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <div className="storage-size">{formatStorage(sto)}</div>
                            {priceForStorage && (
                              <div className="storage-price">
                                ₹{priceForStorage.toLocaleString()}
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                <motion.div className="cart-row" variants={fadeUp}>
                  <div className="qty">
                    <motion.button onClick={() => setQty(qty > 1 ? qty - 1 : 1)}>
                      −
                    </motion.button>
                    <span>{qty}</span>
                    <motion.button onClick={() => setQty(qty + 1)}>
                      +
                    </motion.button>
                  </div>

                  <motion.button
                    className="btn btn-outline-dark"
                    onClick={handleAddToCart}
                  >
                    Add to cart
                  </motion.button>
                </motion.div>

                {selectedVariant?.sku && (
                  <motion.p className="mt-2 text-muted small" variants={fadeUp}>
                    SKU: {selectedVariant.sku}
                  </motion.p>
                )}
              </motion.div>
            </Col>
          </Row>
        </Container>

        {relatedProducts.length > 0 && !loadingRelated && (
          <Container className="related-products-section">
            <motion.h3
              className="related-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              You May Also Like
            </motion.h3>

            <Row>
              {relatedProducts.map((relatedProduct, index) => {
                const productImage = getRelatedProductImage(relatedProduct);
                const productPrice = getRelatedProductPrice(relatedProduct);
                const productId = relatedProduct._id?.$oid || relatedProduct._id;

                return (
                  <Col lg={3} md={4} sm={6} xs={6} key={productId}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                      className="related-product-card"
                    >
                      <NavLink
                        to={`/product-details/${productId}`}
                        className="related-product-link"
                      >
                        <div className="related-product-image">
                          <img
                            src={productImage}
                            alt={relatedProduct.name}
                            onError={(e) => (e.target.src = "/placeholder.jpg")}
                          />
                        </div>
                        <h6 className="related-product-name">{relatedProduct.name}</h6>
                        <div className="related-product-price">
                          {productPrice}
                        </div>
                      </NavLink>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          </Container>
        )}
      </motion.div>

      <Footer />
    </div>
  );
};

export default Productdetails;